import React from "react";
import { Alert, CircularProgress } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReplayIcon from "@mui/icons-material/Replay";
import GestureIcon from "@mui/icons-material/Gesture";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CustomButton from "../../../../components/buttons/CustomButton";
import CheckStatusList from "./CheckStatusList";
import { formatConfidence } from "../lib/formatMeasurement";
import styles from "./CranialSteps.module.css";

const LOW_CONFIDENCE = 0.5;

const PROCESSING_NOTICE = "A foto será enviada para processamento e não fica armazenada.";

const VISUAL_CHECKS = [
  { key: "headVisibility", label: "Cabeça inteira visível" },
  { key: "perspective", label: "Câmera paralela à cabeça" },
  { key: "contourVisibility", label: "Contorno do crânio visível" },
  { key: "noseVisibility", label: "Nariz visível para orientação" },
  { key: "earVisibility", label: "Orelhas visíveis" },
  { key: "hairInterference", label: "Cabelo sem cobrir o contorno" },
];

export const AI_WARNING_LABELS = {
  HEAD_CROPPED: "Parte da cabeça parece estar fora da foto.",
  PERSPECTIVE_UNCERTAIN: "A câmera parece inclinada em relação à cabeça.",
  CONTOUR_HIDDEN: "Parte do contorno do crânio não está visível.",
  NOSE_NOT_VISIBLE: "O nariz não está visível; confira a orientação do eixo.",
  EARS_NOT_VISIBLE: "Orelhas não totalmente visíveis. Ajuste os marcos das orelhas no editor.",
  HAIR_INTERFERENCE: "O cabelo pode estar alterando o contorno.",
  LOW_CONTRAST: "Há pouco contraste entre a cabeça e o fundo.",
  MARKER_OFF_PLANE: "O marcador de escala parece fora do plano da cabeça.",
  LOW_CONFIDENCE: "A sugestão automática tem baixa confiança.",
};

export const aiWarningText = (warning) =>
  AI_WARNING_LABELS[warning && warning.code] || (warning && warning.message) || "";

/**
 * AI analysis step: processing notice while waiting, then the visual checks,
 * warnings and confidence of the suggestion, or a recoverable error.
 */
function AiAnalysisStep({ isAnalyzing, aiSuggestion, error, sourceType, onRetry, onManual, onContinue, onRetake }) {
  if (isAnalyzing) {
    return (
      <div className={styles.card}>
        <div className={styles.loadingBlock}>
          <CircularProgress />
          <h3 className={styles.cardTitle}>Analisando a foto...</h3>
          <p className={styles.cardText}>
            Identificando o contorno da cabeça, o nariz, as orelhas e um eventual marcador de escala. Isso pode levar alguns segundos.
          </p>
          <div className={styles.notice} style={{ marginTop: 0 }}>
            <LockOutlinedIcon fontSize="small" className={styles.noticeIcon} />
            <span>{PROCESSING_NOTICE}</span>
          </div>
        </div>
      </div>
    );
  }

  const retakeButton = (
    <CustomButton
      variant="outlined"
      style="primary"
      label={sourceType === "camera" ? "Tirar outra foto" : "Escolher outra imagem"}
      icon={sourceType === "camera" ? <PhotoCameraOutlinedIcon /> : <FileUploadOutlinedIcon />}
      onClick={onRetake}
    />
  );

  if (error) {
    const warnings = (error.warnings || []).map(aiWarningText).filter(Boolean);

    return (
      <div className={styles.card}>
        <span className={styles.eyebrow}>Análise automática</span>
        <Alert severity="warning" style={{ borderRadius: 12, marginTop: "0.5rem" }}>
          {error.message}
          {warnings.length > 0 && (
            <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.1rem" }}>
              {warnings.map((text) => <li key={text}>{text}</li>)}
            </ul>
          )}
        </Alert>
        <div className={styles.actions}>
          {error.backTo === "source" && retakeButton}
          {error.canRetry && (
            <CustomButton variant="outlined" style="primary" label="Tentar novamente" icon={<ReplayIcon />} onClick={onRetry} />
          )}
          {error.canTraceManually && (
            <CustomButton variant="contained" style="primary" label="Marcar manualmente" icon={<GestureIcon />} onClick={onManual} />
          )}
        </div>
      </div>
    );
  }

  if (!aiSuggestion) return null;

  const checks = aiSuggestion.imageChecks || {};
  const warnings = aiSuggestion.warnings || [];
  const items = VISUAL_CHECKS.map((check) => ({ ...check, status: checks[check.key] || null }))
    .filter((item) => item.status);
  const isLowConfidence = typeof aiSuggestion.confidence === "number" && aiSuggestion.confidence < LOW_CONFIDENCE;
  const markerDetected = aiSuggestion.calibrationSuggestion && aiSuggestion.calibrationSuggestion.status === "detected";

  return (
    <div className={styles.card}>
      <span className={styles.eyebrow}>Análise automática</span>
      <h3 className={styles.cardTitle}>Sugestão pronta para revisão</h3>
      <p className={styles.cardText}>
        A análise sugeriu o contorno da cabeça e os marcos do nariz e das orelhas. Confira e ajuste no editor antes de ver as medidas.
      </p>

      <div className={styles.statRow}>
        <div className={styles.stat}>
          <div className={styles.statLabel}>Confiança da sugestão</div>
          <div className={styles.statValue}>{formatConfidence(aiSuggestion.confidence)}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>Marcador de escala</div>
          <div className={styles.statValue}>{markerDetected ? "Identificado" : "Não identificado"}</div>
        </div>
      </div>

      {items.length > 0 && <CheckStatusList items={items} />}

      {warnings.length > 0 && (
        <Alert severity="warning" style={{ borderRadius: 12, marginTop: "1rem" }}>
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {warnings.map((warning, index) => <li key={`${warning.code}-${index}`}>{aiWarningText(warning)}</li>)}
          </ul>
        </Alert>
      )}

      {isLowConfidence && (
        <Alert severity="info" style={{ borderRadius: 12, marginTop: "1rem" }}>
          A confiança da sugestão é baixa. Revise o traçado com atenção, tente novamente ou marque manualmente.
        </Alert>
      )}

      <div className={styles.actions}>
        {isLowConfidence && (
          <>
            <CustomButton variant="outlined" style="primary" label="Tentar novamente" icon={<ReplayIcon />} onClick={onRetry} />
            <CustomButton variant="outlined" style="primary" label="Marcar manualmente" icon={<GestureIcon />} onClick={onManual} />
          </>
        )}
        <CustomButton variant="contained" style="primary" label="Ajustar no editor" icon={<ArrowForwardIcon />} onClick={onContinue} />
      </div>
    </div>
  );
}

export default AiAnalysisStep;
